/**
 * PrintDialog - PDF export tutorial dialog
 * Migrated from front/src/components/PrintDialog.js
 * Shows platform-specific instructions for PDF export
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

// Import tutorial images
import computer1 from '../assets/images/pdf_tutorial/computer_1.png';
import computer2 from '../assets/images/pdf_tutorial/computer_2.png';
import ios1 from '../assets/images/pdf_tutorial/ios_1.png';
import android1 from '../assets/images/pdf_tutorial/android_1.jpg';

import { print } from '../config/settings';

/**
 * iOS platform detection keywords
 */
const iosKeywords = new Set([
  'iPad Simulator',
  'iPhone Simulator',
  'iPod Simulator',
  'iPad',
  'iPhone',
  'iPod',
]);

/**
 * Detect current platform for tutorial display
 * @param {boolean} isMobile - Whether the device is mobile
 * @returns {string} Platform identifier ('iOS', 'Android', or 'Computer')
 */
const getPlatform = isMobile => {
  const isIos = typeof globalThis !== 'undefined' && iosKeywords.has(navigator.platform);
  if (isIos) return 'iOS';
  if (isMobile) return 'Android';
  return 'Computer';
};

/**
 * Tutorial content configuration by platform
 */
const tutorials = {
  iOS: {
    text: print.tutorial.ios,
    imgs: [ios1],
  },
  Android: {
    text: print.tutorial.android,
    imgs: [android1],
  },
  Computer: {
    text: print.tutorial.computer,
    imgs: [computer1, computer2],
  },
};

/**
 * PrintDialog component - Shows PDF export tutorial
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Handler for dialog close
 * @returns {JSX.Element} PrintDialog component
 */
const PrintDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const isVerySmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [platform, setPlatform] = useState(getPlatform(isVerySmallScreen));
  const [dontShow, setDontShow] = useState(false);

  useEffect(() => {
    setPlatform(getPlatform(isVerySmallScreen));
  }, [isVerySmallScreen]);

  /**
   * Handle print action and close dialog
   */
  const printAndCloseDialog = () => {
    localStorage.setItem('CLIMADIAG_hide_tutorial', dontShow);
    globalThis.print();
    onClose();
  };

  return (
    <Dialog
      disableRestoreFocus
      open={open}
      fullScreen={isVerySmallScreen}
      sx={{ '@media print': { display: 'none' } }}
    >
      <DialogTitle id="alert-dialog-title">
        Comment exporter la synthèse en PDF
      </DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={platform}>
              <Tab
                value="Computer"
                label="Ordinateur"
                sx={{ textTransform: 'none' }}
                onClick={() => setPlatform('Computer')}
              />
              <Tab
                value="Android"
                label="Android"
                sx={{ textTransform: 'none' }}
                onClick={() => setPlatform('Android')}
              />
              <Tab
                value="iOS"
                label="iOS"
                sx={{ textTransform: 'none' }}
                onClick={() => setPlatform('iOS')}
              />
            </Tabs>
          </Box>
          <Box>
            <Typography sx={{ my: 2, whiteSpace: 'pre-wrap' }}>
              {tutorials[platform].text}
            </Typography>
            {/* Images tutorial disabled for now
            <Stack direction="column" alignItems="center">
              {tutorials[platform].imgs.map(img => (
                <Paper
                  elevation={3}
                  component="img"
                  src={img}
                  sx={{ mb: 5, maxWidth: '80%', boxShadow: '' }}
                />
              ))}
            </Stack> */}
          </Box>
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'space-evenly', flexWrap: 'wrap' }}>
        <FormControlLabel
          sx={{ ml: 2 }}
          value={dontShow}
          onChange={e => setDontShow(e.target.checked)}
          control={<Checkbox />}
          label="Ne plus afficher"
        />
        <Box>
          <Button onClick={onClose}>Annuler</Button>
          <Button sx={{ mx: 2 }} variant="contained" onClick={printAndCloseDialog}>
            Télécharger
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

PrintDialog.displayName = 'PrintDialog';

export default PrintDialog;
